import { TableCell, TableRow } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { Column, Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

/** Totals keyed by column id, already formatted the way the column's cells are. */
export type DataTableTotals = Partial<Record<string, React.ReactNode>>

type TotalsRowProps<TData> = {
  table: Table<TData>
  totals: DataTableTotals
  /** Sticky positioning for pinned columns, mirrored from the body cells. */
  getCellAttributes?: (column: Column<TData, unknown>) => {
    className?: string
    style?: React.CSSProperties
  }
}

/**
 * Pinned first body row carrying the totals across every page for the current filters. Lives
 * outside the TanStack row model on purpose: sorting, pagination, selection and expansion never
 * reach it. The first visible column without a total carries the "Total" label.
 */
export function TotalsRow<TData>({ table, totals, getCellAttributes }: TotalsRowProps<TData>) {
  const { t } = useTranslation('common')
  const columns = table.getVisibleLeafColumns()
  const labelColumnId = columns.find((column) => totals[column.id] === undefined)?.id

  return (
    <TableRow
      data-status="default"
      data-testid="datatable-totals-row"
      className="group/row bg-background-level-2/80 hover:bg-background-level-2/80 font-semibold"
    >
      {columns.map((column) => {
        const attributes = getCellAttributes?.(column) ?? {}
        return (
          <TableCell
            key={column.id}
            className={cn(column.columnDef.meta?.cellClassName, attributes.className)}
            style={attributes.style}
          >
            {column.id === labelColumnId ? t('datatable.totalRow') : totals[column.id]}
          </TableCell>
        )
      })}
    </TableRow>
  )
}
